import { Page } from "model/page";
import { PageType } from "type/page-type";

/**
 * The IDX Pages section's view of the client's pages (MAX-4419 task 8, OSEO 1, 2, 12, 13; FD-VPLAT-26).
 *
 * Every permitted page is listed by category, one accordion each (Figma `3574:32905`), with the
 * Default IDX Page pinned at the top of the first. With no stored setup state yet (SPFF-11), "set up"
 * is `enabled`. `enabled` stays the switch, and nothing is created or deleted from Control Panel
 * (Dave, from Angelica's update; this replaces interim D3).
 */

interface CatalogEntry {
	type: PageType;
	/** Shown in the table and the Edit modal title (OSEO 11 names). */
	label: string;
	/** The table's Description column (OSEO 11, verbatim). */
	description: string;
}

interface Category {
	label: string;
	entries: CatalogEntry[];
}

// The four categories in OSEO 11's order. Build correction: one category name, "Team & Offices Pages"
// (OSEO 11 and `3574:32905` each spell it two ways).
export const CATEGORIES: Category[] = [
	{
		label: "Search & Results",
		entries: [
			{ type: PageType.LISTINGS, label: "Search", description: "Main IDX search and results page" },
			{ type: PageType.LISTING, label: "Listing Details", description: "Full detail page for a single listing" },
		],
	},
	{
		label: "Featured & Markets",
		entries: [
			{ type: PageType.FEATURED_LISTINGS, label: "Featured Listings", description: "The agent's/office's own active listings" },
			{ type: PageType.PENDING_FEATURED_LISTINGS, label: "Pending Featured Listings", description: "The agent's/office's own pending listings" },
			{ type: PageType.SOLD_FEATURED_LISTINGS, label: "Sold Featured Listings", description: "The agent's/office's own sold listings (last 2 years)" },
			{ type: PageType.OPEN_HOME_LISTINGS, label: "Open House Listings", description: "Upcoming open houses across active listings" },
			{ type: PageType.SUPPLEMENTAL_LISTINGS, label: "Supplemental Listings", description: "Own pocket/out-of-board listings, added manually" },
			{ type: PageType.HOT_SHEETS, label: "Markets", description: "Directory of the agent's saved-search areas" },
			{ type: PageType.LISTING_REPORT, label: "Listing Report", description: "Current listings within a Market" },
			{ type: PageType.OPEN_HOME_REPORT, label: "Open House Report", description: "Open houses within a Market" },
			{ type: PageType.MARKET_REPORT, label: "Market Report", description: "Price, inventory, and days-on-market stats for a Market" },
		],
	},
	{
		label: "Team & Offices Pages",
		entries: [
			{ type: PageType.AGENTS, label: "Team Member List", description: "Directory of all team members" },
			{ type: PageType.AGENT, label: "Team Member Bio", description: "One team member’s bio, contact info, and listings" },
			{ type: PageType.OFFICES, label: "Offices List", description: "Directory of all offices" },
			{ type: PageType.OFFICE, label: "Office Detail", description: "One office's address, contact info, and team members" },
		],
	},
	{
		label: "Lead Capture Forms & Tools",
		entries: [
			{ type: PageType.PROPERTY_ORGANIZER, label: "Property Organizer", description: "Where leads manage saved listings and searches" },
			{ type: PageType.EMAIL_ALERTS, label: "Email Alerts", description: "Where leads sign up for saved search email alerts" },
			{ type: PageType.MORTGAGE_CALCULATOR, label: "Mortgage Calculator", description: "Where leads estimate a monthly payment" },
			{ type: PageType.VALUATION_FORM, label: "Valuation Form", description: "Where leads request a home value estimate" },
			{ type: PageType.CONTACT_FORM, label: "Contact Form", description: "Where leads send a general inquiry" },
		],
	},
];

/**
 * Today's Default page (type 1). Every disabled page's links route through it
 * (ihf-root KestrelURLResolver), so it is always in use and can't be turned off or removed here.
 */
export const STATUS_PAGE_LABEL = "Default IDX Page";
export const STATUS_PAGE_DESCRIPTION = "Required page that hosts all IDX content";

// S1: the three pages that carry the "Recommended" tag.
const RECOMMENDED_TYPES = [PageType.LISTINGS, PageType.LISTING, PageType.FEATURED_LISTINGS];

const ENTRIES = CATEGORIES.flatMap((category) => category.entries);

export function isStatusPage(page: Page): boolean {
	return page.type === PageType.DEFAULT;
}

export function pageLabel(page: Page): string {
	if (isStatusPage(page)) {
		return STATUS_PAGE_LABEL;
	}
	const entry = ENTRIES.find((entry) => entry.type === page.type);
	return entry ? entry.label : page.type.label;
}

export function isRecommended(page: Page): boolean {
	return RECOMMENDED_TYPES.includes(page.type);
}

export function pageDescription(page: Page): string {
	if (isStatusPage(page)) {
		return STATUS_PAGE_DESCRIPTION;
	}
	return ENTRIES.find((entry) => entry.type === page.type)?.description || "";
}

export interface CategoryGroup {
	label: string;
	pages: Page[];
	/** Pages set up: `enabled`, plus the Default IDX Page where it is pinned, which is always in use. */
	active: number;
	/** Pages in the category, counting the Default IDX Page where it is pinned. */
	total: number;
	/** Recommended pages that aren't enabled yet (the header's warning). */
	recommendedNotSetUp: number;
}

/** Where a page type the catalogue doesn't know yet goes, so it can still be seen and edited. */
export const UNCATALOGUED_LABEL = "Other";

function group(label: string, categoryPages: Page[], statusPage?: Page): CategoryGroup {
	return {
		label,
		pages: statusPage ? [statusPage, ...categoryPages] : categoryPages,
		// The Default IDX Page counts as set up whatever its stored `enabled`: the table always shows it Enabled.
		active: categoryPages.filter((page) => page.enabled).length + (statusPage ? 1 : 0),
		total: categoryPages.length + (statusPage ? 1 : 0),
		recommendedNotSetUp: categoryPages.filter((page) => isRecommended(page) && !page.enabled).length,
	};
}

/**
 * One group per category that has any of the client's pages, in catalogue order. The Default IDX
 * Page leads the first group and counts there as one set-up page: it's always in use.
 * Any page type the catalogue doesn't list yet lands in a trailing "Other" group rather than
 * disappearing, as the old table kept them at its end.
 */
export function categoryGroups(pages: Page[]): CategoryGroup[] {
	const statusPage = pages.find(isStatusPage);
	const groups = CATEGORIES.map((category, index) =>
		group(
			category.label,
			category.entries
				.map((entry) => pages.find((page) => page.type === entry.type))
				.filter((page): page is Page => !!page),
			index === 0 ? statusPage : undefined,
		),
	);
	const uncatalogued = pages.filter((page) => !isStatusPage(page) && !ENTRIES.some((entry) => entry.type === page.type));
	if (uncatalogued.length) {
		groups.push(group(UNCATALOGUED_LABEL, uncatalogued));
	}
	return groups.filter((group) => group.pages.length > 0);
}

/**
 * A stored path splits into the editable slug and a fixed query. Some types carry query variables
 * the page needs to load its content (e.g. `/listing?id={id}`); only the slug is editable (OSEO 13),
 * so the query is kept as it is.
 */
export function splitPath(path: string): { slug: string; query: string } {
	const [pathname, ...rest] = (path || "").split("?");
	return {
		slug: pathname.replace(/^\/+/, ""),
		query: rest.length ? `?${rest.join("?")}` : "",
	};
}

/** Normalised so formatting alone (a leading or trailing slash, spaces) isn't a change. */
export function normalizeSlug(slug: string): string {
	return slug.trim().replace(/^\/+/, "").replace(/\/+$/, "");
}

/**
 * The path a typed slug saves as: exactly as typed, with a leading slash added when it has none, as
 * the old SEO Pages editor saved it (MAX-4577). The clean-up in `normalizeSlug` is only for
 * deciding whether the path changed, never for what is written.
 */
export function pathToSave(slug: string): string {
	return slug.startsWith("/") ? slug : `/${slug}`;
}

/** Whether the type's default path carries query variables the page needs to load (`/listing?id={id}`). */
export function hasQueryVariables(page: Page): boolean {
	return !!page.type.defaultPath && splitPath(page.type.defaultPath).query !== "";
}

/**
 * The inline validation DefaultPagePath and the old SEO Pages editor had. An empty slug would save
 * the path "/", the site's home page; on the Default IDX Page that sends every disabled page's links
 * there (KestrelURLResolver), and DefaultPagePath refused it. A "?" is refused too when `refuseQuery`:
 * the Default IDX Page is a base that "?path=" gets appended to. Any other page's path may carry a
 * query: its own variables (`/listing?id={id}`), or a site's (index.php?page_id=12).
 */
export function slugError(slug: string, refuseQuery: boolean): string | undefined {
	// Judged on the part before any "?": a slug that is only a query would save the home page (/?page_id=12).
	if (!normalizeSlug(slug.split("?")[0])) {
		return "Enter the page's URL path";
	}
	if (refuseQuery && slug.includes("?")) {
		return "Cannot contain a \"?\"";
	}
	if (slug.includes("//")) {
		return "Cannot have two or more \"/\"s in a row";
	}
	return undefined;
}

/**
 * Where a page's links go while it isn't enabled: through the Default IDX Page, with the page's
 * path in `?path=`. It mirrors ihf-root's `KestrelURLResolver.getPattern`, which appends the page's
 * path with every `?` turned into `&`, so `/listing?id={id}` reads `/idx?path=/listing&id={id}`.
 * The query variables are filled in per link.
 */
export function statusPageUrl(baseUrl: string, statusPagePath: string, path: string): string {
	const { slug, query } = splitPath(path);
	return `${displayUrl(baseUrl, statusPagePath)}?path=/${slug}${query.replace(/\?/g, "&")}`;
}

/** A URL without its scheme, as the Edit modal quotes the page address in prose (www.example.com/idx). */
export function bareUrl(url: string): string {
	const scheme = url.indexOf("://");
	return scheme === -1 ? url : url.slice(scheme + 3);
}

/** What the table shows: the base URL plus the path, without the query variables. */
export function displayUrl(baseUrl: string, path: string): string {
	return `${(baseUrl || "").replace(/\/+$/, "")}/${splitPath(path).slug}`;
}

/** Appends the noun ("Page" / "page") to a page's label unless it already ends in it, so the Default IDX Page isn't "Default IDX Page Page". */
export function withPageNoun(label: string, noun: "Page" | "page"): string {
	return /\bpage$/i.test(label.trim()) ? label : `${label} ${noun}`;
}

/** A page whose Action is "Set Up": not the Default IDX Page (always in use) and not enabled. */
export function isSettingUp(page: Page): boolean {
	return !isStatusPage(page) && !page.enabled;
}

/**
 * The path before its first "?" with a leading slash: where the client creates the page, as the old
 * editor's "Create a page at" showed it (`/listing?id={id}` reads `/listing`).
 */
export function displaySlug(path: string): string {
	return `/${splitPath(path).slug}`;
}

/** The page type's default slug (ihf-root's `ClientPageType`), or undefined for a type the catalogue doesn't know. */
export function defaultSlug(page: Page): string | undefined {
	return page.type.defaultPath ? displaySlug(page.type.defaultPath) : undefined;
}
